import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import styles from "./AdminRoute.module.css";

type Props = { children: ReactNode };

// Defence-in-depth wrapper for /dashboard/admin/* routes. The authoritative
// gate is the backend URL matcher (hasRole("ADMIN") on /api/admin/**) — this
// component just stops a curious non-admin from ever seeing the empty admin
// UI shell. The `isAdmin` flag is derived server-side from Cognito group
// membership and cannot be forged by the client.
export const AdminRoute = ({ children }: Props): JSX.Element => {
  const { status, user } = useAuth();

  if (status === "loading") {
    return (
      <div className={styles.gate}>
        <p className={styles.gateText}>Loading…</p>
      </div>
    );
  }

  if (status !== "authenticated") {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isAdmin) {
    return (
      <div className={styles.forbidden} role="alert">
        <div className={styles.forbiddenInner}>
          <span className={styles.forbiddenEyebrow}>// 403 — ADMIN ONLY</span>
          <h1 className={styles.forbiddenTitle}>Restricted area</h1>
          <p className={styles.forbiddenBody}>
            This page is reserved for ZeroToClaw administrators. Your account
            doesn't have access. If you think this is a mistake, ping the
            team.
          </p>
          <Link to="/dashboard/overview" className={styles.forbiddenCta}>
            Back to dashboard
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
